import { ref, computed, onMounted, watch } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import Header from '@/components/HeaderDashboard.vue';
import Swal from 'sweetalert2';
import { hasPermission } from '@/store/userStore';
import { CategoriaService } from '@/services/CategoriaService';
import { DepartamentoService } from '@/services/DepartamentoService';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, DownloadIcon, FileTextIcon, GripVerticalIcon, InfoIcon, PowerIcon, Loader2Icon, LayersIcon } from 'lucide-vue-next';
import draggable from 'vuedraggable';
export default {
    name: 'Categorias',
    components: { Sidebar, Header, PlusIcon, EditIcon, TrashIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, DownloadIcon, FileTextIcon, GripVerticalIcon, InfoIcon, draggable, PowerIcon, Loader2Icon, LayersIcon },
    setup() {
        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        const categorias = ref([]);
        const departamentos = ref([]);
        const isLoading = ref(false);
        const isSubmitting = ref(false);
        const isModalOpen = ref(false);
        const isEditing = ref(false);
        const currentId = ref(null);
        const searchQuery = ref('');
        const currentPage = ref(1);
        const totalPages = ref(1);
        const totalCount = ref(0);
        const itemsPerPage = 10;
        const currentForm = ref({ nombre: '', departamentoID: 0, active: true });
        // Alphabet filter
        const selectedLetter = ref('');
        // Filter Controls
        const filterDepartamentoId = ref(null);
        const searchFilterDep = ref('');
        const showFilterDeps = ref(false);
        function resetFilter() {
            selectedLetter.value = '';
            searchQuery.value = '';
            filterDepartamentoId.value = null;
            searchFilterDep.value = '';
            loadItems();
        }
        // Export Excel State
        const isExportModalOpen = ref(false);
        const isExporting = ref(false);
        const availableColumns = ref([
            { id: 1, name: "Nombre" },
            { id: 2, name: "Departamento" }
        ]);
        const exportConfig = ref({
            format: 'excel',
            selectedColumns: ["Nombre", "Departamento"],
            sortColumn: "Nombre",
            sortDirection: "asc"
        });
        const searchDepartamentoText = ref('');
        const showDepartamentoDropdown = ref(false);
        function getNombreDepartamento(id) {
            return departamentos.value.find(d => d.departamentoID === id)?.nombre ?? '—';
        }
        function formatDate(dateStr) {
            if (!dateStr)
                return '—';
            return new Date(dateStr).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        let debounceTimeout = null;
        watch([searchQuery, selectedLetter, filterDepartamentoId], () => {
            currentPage.value = 1;
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
                loadItems();
            }, 300);
        });
        watch(searchFilterDep, (newVal) => {
            if (newVal.trim() === '' && filterDepartamentoId.value !== null) {
                filterDepartamentoId.value = null;
            }
        });
        const filterByLetter = (letter) => {
            selectedLetter.value = letter;
            searchQuery.value = '';
        };
        const filteredDepartamentosForSelect = computed(() => {
            if (searchDepartamentoText.value.length < 2)
                return [];
            const searchTerm = searchDepartamentoText.value.toLowerCase();
            return departamentos.value
                .filter(d => d.nombre.toLowerCase().includes(searchTerm))
                .map(d => {
                const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi");
                const highlightedNombre = d.nombre.replace(regex, `<mark>$1</mark>`);
                return { ...d, highlightedNombre };
            })
                .sort((a, b) => a.nombre.localeCompare(b.nombre));
        });
        const onSearchDepartamento = () => {
            showDepartamentoDropdown.value = true;
        };
        const selectDepartamento = (dep) => {
            currentForm.value.departamentoID = dep.departamentoID;
            searchDepartamentoText.value = dep.nombre;
            showDepartamentoDropdown.value = false;
        };
        const filteredFilterDepsList = computed(() => {
            let deps = departamentos.value;
            if (searchFilterDep.value) {
                const q = searchFilterDep.value.toLowerCase();
                deps = deps.filter(d => d.nombre.toLowerCase().includes(q));
            }
            return deps.slice(0, 10).map(d => {
                if (!searchFilterDep.value)
                    return { ...d, highlightedNombre: d.nombre };
                const regex = new RegExp(`(${searchFilterDep.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                const highlightedNombre = d.nombre.replace(regex, `<mark>$1</mark>`);
                return { ...d, highlightedNombre };
            });
        });
        const selectFilterDep = (dep) => {
            filterDepartamentoId.value = dep.departamentoID;
            searchFilterDep.value = dep.nombre;
            showFilterDeps.value = false;
            loadItems();
        };
        const clearFilterDep = () => {
            filterDepartamentoId.value = null;
            searchFilterDep.value = '';
            showFilterDeps.value = false;
            loadItems();
        };
        const onBlurFilterDep = () => {
            setTimeout(() => {
                showFilterDeps.value = false;
                if (searchFilterDep.value.trim() === '') {
                    filterDepartamentoId.value = null;
                    loadItems();
                }
                else {
                    const selected = departamentos.value.find(d => d.departamentoID === filterDepartamentoId.value);
                    if (selected) {
                        searchFilterDep.value = selected.nombre;
                    }
                    else {
                        searchFilterDep.value = '';
                        filterDepartamentoId.value = null;
                        loadItems();
                    }
                }
            }, 150);
        };
        async function loadItems() {
            isLoading.value = true;
            try {
                const response = await CategoriaService.getPaginated(currentPage.value, searchQuery.value, selectedLetter.value, filterDepartamentoId.value || undefined);
                categorias.value = response.items || response.Items || [];
                totalCount.value = response.totalCount || response.TotalCount || 0;
                totalPages.value = response.totalPages || response.TotalPages || 1;
            }
            catch {
                Swal.fire({ title: 'Error', text: 'No se pudieron cargar las categorías.', icon: 'error' });
            }
            finally {
                isLoading.value = false;
            }
        }
        function prevPage() {
            if (currentPage.value > 1) {
                currentPage.value--;
                loadItems();
            }
        }
        function nextPage() {
            if (currentPage.value < totalPages.value) {
                currentPage.value++;
                loadItems();
            }
        }
        async function loadDepartamentos() {
            try {
                departamentos.value = await DepartamentoService.getAll();
            }
            catch {
                console.error('Error cargando departamentos');
            }
        }
        function openModal(cat) {
            if (cat) {
                currentForm.value = { nombre: cat.nombre, departamentoID: cat.departamentoID, active: cat.active !== false };
                currentId.value = cat.categoriaID;
                isEditing.value = true;
                searchDepartamentoText.value = getNombreDepartamento(cat.departamentoID);
            }
            else {
                currentForm.value = { nombre: '', departamentoID: 0, active: true };
                currentId.value = null;
                isEditing.value = false;
                searchDepartamentoText.value = '';
            }
            isModalOpen.value = true;
            showDepartamentoDropdown.value = false;
        }
        function closeModal() { isModalOpen.value = false; }
        // Export functions
        function openExportModal() {
            isExportModalOpen.value = true;
        }
        function closeExportModal() {
            isExportModalOpen.value = false;
        }
        async function handleExport() {
            if (exportConfig.value.selectedColumns.length === 0) {
                Swal.fire({ title: 'Atención', text: 'Debe seleccionar al menos una columna.', icon: 'warning' });
                return;
            }
            isExporting.value = true;
            try {
                await CategoriaService.exportReport({
                    departamentoId: filterDepartamentoId.value || null,
                    searchTerm: searchQuery.value,
                    selectedColumns: availableColumns.value
                        .map(c => c.name)
                        .filter(name => exportConfig.value.selectedColumns.includes(name)),
                    format: exportConfig.value.format,
                    sortColumn: exportConfig.value.sortColumn,
                    sortDirection: exportConfig.value.sortDirection
                });
                closeExportModal();
                Swal.fire({ title: '¡Éxito!', text: 'Reporte generado correctamente.', icon: 'success', timer: 2000, showConfirmButton: false });
            }
            catch (error) {
                Swal.fire({ title: 'Error', text: 'No se pudo generar el reporte.', icon: 'error' });
            }
            finally {
                isExporting.value = false;
            }
        }
        async function saveItem() {
            isSubmitting.value = true;
            try {
                const payload = {
                    categoriaID: currentId.value ?? 0,
                    nombre: currentForm.value.nombre,
                    departamentoID: currentForm.value.departamentoID,
                    active: currentForm.value.active !== false,
                    fechaCreacion: new Date().toISOString(),
                };
                if (isEditing.value && currentId.value !== null) {
                    await CategoriaService.update(currentId.value, payload);
                    Swal.fire({ title: '¡Actualizada!', text: 'Categoría actualizada correctamente.', icon: 'success', timer: 2000, showConfirmButton: false });
                }
                else {
                    await CategoriaService.add(payload);
                    Swal.fire({ title: '¡Registrada!', text: 'Categoría agregada correctamente.', icon: 'success', timer: 2000, showConfirmButton: false });
                }
                closeModal();
                await loadItems();
            }
            catch {
                Swal.fire({ title: 'Error', text: 'Hubo un problema al guardar la categoría.', icon: 'error' });
            }
            finally {
                isSubmitting.value = false;
            }
        }
        async function confirmDelete(id) {
            const res = await Swal.fire({
                title: '¿Desea eliminar esta categoría?',
                text: 'Esta acción no se puede deshacer.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
            });
            if (res.isConfirmed) {
                try {
                    await CategoriaService.delete(id);
                    Swal.fire({ title: '¡Eliminada!', text: 'La categoría ha sido eliminada.', icon: 'success', timer: 2000, showConfirmButton: false });
                    await loadItems();
                }
                catch (e) {
                    Swal.fire({
                        title: 'No se puede eliminar',
                        text: e?.message || 'No se pudo eliminar la categoría.',
                        icon: 'error',
                        confirmButtonText: 'Entendido'
                    });
                }
            }
        }
        async function toggleActive(cat) {
            const result = await Swal.fire({
                title: '¿Cambiar estado?',
                text: `¿Estás seguro de que deseas ${cat.active ? 'desactivar' : 'activar'} la categoría "${cat.nombre}"?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
            });
            if (!result.isConfirmed)
                return;
            try {
                await CategoriaService.toggleActivo(cat.categoriaID);
                cat.active = !cat.active;
                Swal.fire({
                    title: cat.active ? 'Habilitado' : 'Deshabilitado',
                    text: `La categoría ahora está ${cat.active ? 'activa' : 'inactiva'}.`,
                    icon: 'success',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            }
            catch (err) {
                Swal.fire({ title: 'Error', text: err.message || 'No se pudo cambiar el estado', icon: 'error' });
            }
        }
        onMounted(async () => {
            await loadDepartamentos();
            await loadItems();
        });
        return {
            hasPermission,
            categorias, departamentos, isLoading, isSubmitting, isModalOpen, isEditing, currentId,
            searchQuery, currentPage, totalPages, totalCount,
            paginatedItems: categorias, filteredCategorias: categorias,
            selectedLetter, resetFilter,
            filterDepartamentoId, searchFilterDep, showFilterDeps, filteredFilterDepsList, selectFilterDep, clearFilterDep, onBlurFilterDep,
            currentForm, searchDepartamentoText, showDepartamentoDropdown, filteredDepartamentosForSelect,
            onSearchDepartamento, selectDepartamento, loadItems, loadDepartamentos, openModal, closeModal, saveItem, confirmDelete,
            getNombreDepartamento, formatDate, toggleActive, filterByLetter,
            isExportModalOpen, isExporting, availableColumns, exportConfig, openExportModal, closeExportModal, handleExport,
            prevPage, nextPage, alphabet
        };
    },
};
